// Google Analytics 4 loader, consent-first. The gtag stub and default-denied
// consent state ship in index.html so measurement calls before consent still
// buffer correctly. This module is the runtime side of the same story:
// - Reads the persisted consent choice from localStorage.
// - Injects the gtag.js script only when analytics consent is granted.
// - Exposes `setAnalyticsConsent()` for the consent banner to call on
//   accept or decline.
// - Exposes `trackNetworkJourney()` so page components can log outbound
//   clicks to sister-network properties (matches the analytics event we
//   previously fired via Plausible).
//
// Nothing here writes cookies or fetches from Google until consent is
// granted, so the site is compliant with GDPR / CCPA under the strict
// interpretation.

export const GA_MEASUREMENT_ID = "G-T69T5T4E02";

/**
 * Shrink Network domain to GA4 measurement ID. Used for the cross-domain
 * linker and as the `send_to` target of network_journey_in, so the
 * destination site's property records the arrival. Only this site's own ID
 * goes into gtag("config").
 */
export const SHRINK_NETWORK_GA: Record<string, string> = {
  "shrinkdaily.com": "G-9RS8FJGTY3",
  "shrinkopedia.com": "G-H3QSDSHGHS",
  "shrinktionary.com": "G-2HB7NZWE3R",
  "shrinkmd.com": "G-WNXQ7CQE54",
  "shrinkiatry.com": "G-9S4X8538YM",
  "shrinknetwork.com": "G-VFXN0W0PNP",
  "psychiatryrx.org": "G-M19ZBBKCLG",
  "shariqrefai.com": "G-P568VWDRRY",
  "shrinq.com": "G-RQH4ZC5XYX",
  "anxietyresource.org": "G-T69T5T4E02",
  "depressionresource.org": "G-77KNDWGYP9",
  "anxietyresearch.org": "G-EGSF0SBNSK",
  "beunstuck.app": "G-4TP3L6GTH0",
  "quitscrolling.app": "G-8B6YG4NJYR",
};

const OWN_DOMAIN = "anxietyresource.org";
const LINKER_DOMAINS = Object.keys(SHRINK_NETWORK_GA).filter((d) => d !== OWN_DOMAIN);

/** Maps any hostname (with or without www.) to its network domain. */
export function networkDomainFor(hostname: string): string | undefined {
  const h = hostname.replace(/^www\./, "").toLowerCase();
  return SHRINK_NETWORK_GA[h] ? h : undefined;
}

if (typeof window !== "undefined") {
  (window as Window & { __shrinkNetwork?: Record<string, string> }).__shrinkNetwork =
    SHRINK_NETWORK_GA;
}

const STORAGE_KEY = "ar-analytics-consent";
const SCRIPT_ID = "ga-gtag-script";

export type ConsentChoice = "granted" | "denied";

interface GtagWindow extends Window {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
}

function safeWindow(): GtagWindow | undefined {
  if (typeof window === "undefined") return undefined;
  return window as GtagWindow;
}

function safeLocalStorage(): Storage | undefined {
  try {
    if (typeof window === "undefined") return undefined;
    return window.localStorage;
  } catch {
    // Some browsers throw on `.localStorage` in incognito / disabled modes.
    return undefined;
  }
}

/**
 * Returns the persisted analytics consent choice, or null when the visitor
 * has not yet answered the banner. Callers use null to know whether to
 * render the banner.
 */
export function getStoredConsent(): ConsentChoice | null {
  const ls = safeLocalStorage();
  if (!ls) return null;
  const v = ls.getItem(STORAGE_KEY);
  return v === "granted" || v === "denied" ? v : null;
}

function injectGtagScript() {
  const w = safeWindow();
  if (!w) return;
  if (w.document.getElementById(SCRIPT_ID)) return;
  const s = w.document.createElement("script");
  s.async = true;
  s.id = SCRIPT_ID;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  w.document.head.appendChild(s);
  // Config after the script is queued. gtag() buffers into dataLayer
  // until gtag.js loads and drains it, so ordering here is not critical.
  w.gtag?.("config", GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: true,
    linker: { domains: LINKER_DOMAINS, accept_incoming: true },
  });
}

/**
 * Apply a consent decision. Persists to localStorage, calls
 * gtag("consent", "update", ...) so any measurement calls the app has
 * already buffered can drain, and injects the GA script the first time
 * consent is granted.
 */
export function setAnalyticsConsent(choice: ConsentChoice) {
  const ls = safeLocalStorage();
  if (ls) ls.setItem(STORAGE_KEY, choice);
  const w = safeWindow();
  if (!w) return;
  w.gtag?.("consent", "update", {
    analytics_storage: choice === "granted" ? "granted" : "denied",
  });
  if (choice === "granted") injectGtagScript();
}

/**
 * If the visitor already granted consent on a previous session, load the
 * GA script on mount. Called from App on first render.
 */
export function initAnalyticsFromStoredConsent() {
  if (getStoredConsent() === "granted") injectGtagScript();
}

/**
 * Log a click from this site to another Shrink Network site.
 * network_journey_out lands in this site's property; network_journey_in is
 * sent to the destination's property. Parameters are limited to the
 * destination domain, the page path the click came from, and the UTM
 * campaign slug. Under denied consent gtag records nothing.
 */
export function trackNetworkJourney(destination: string, campaign: string) {
  const w = safeWindow();
  if (!w?.gtag) return;
  const domain = networkDomainFor(destination);
  if (!domain || domain === OWN_DOMAIN) return;
  const params = {
    destination: domain,
    source_site: OWN_DOMAIN,
    source_page: w.location.pathname,
    campaign,
  };
  w.gtag("event", "network_journey_out", { ...params, send_to: GA_MEASUREMENT_ID });
  w.gtag("event", "network_journey_in", { ...params, send_to: SHRINK_NETWORK_GA[domain] });
}

let searchTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Debounced site-search events: `search` for every settled query of three or
 * more characters, plus `search_zero_result` when nothing matched. The query
 * text itself is never sent: a reader's search can name a condition or a
 * medication, and the network standard keeps that out of analytics. Only the
 * query length and the result count go to GA4.
 */
export function trackSearch(query: string, resultCount: number) {
  const q = query.trim();
  if (searchTimer) clearTimeout(searchTimer);
  if (q.length < 3) return;
  searchTimer = setTimeout(() => {
    const w = safeWindow();
    w?.gtag?.("event", "search", { query_length: q.length, results: resultCount });
    if (resultCount === 0) w?.gtag?.("event", "search_zero_result", { query_length: q.length });
  }, 800);
}
